import { combineDateAndTime } from "@mc-intranet/shared";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import moment, { Moment } from "moment";
import React, { useState } from "react";
import { toast } from "react-toastify";
import ApprovementModal from "./punches/ApprovementModal";

interface Props {
  date?: string;
  label: string;
  name: string;
  punchTime?: string;
  disabled?: boolean;
  canEdit?: boolean;
  checkIsTwoHoursFromNow?: boolean;
  inValue?: string;
  minuteStep?: number;
  onChange: (time: Moment | null, name: string) => void;
  userTimeShift?: number;
  fetchUserTimeShift?: () => Promise<any>;
  sendOnApprovement?: (time: Moment, name: string) => void;
  approvementModalState?: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>
  ];
  pendingStatus?: "pending" | "approved" | "rejected" | null;
  pendingTime?: string | null;
}

const TimeSelector: React.FC<Props> = (props) => {
  const {
    date: currentDate,
    label,
    name,
    punchTime,
    disabled,
    minuteStep,
    onChange,
    inValue,
    canEdit,
    checkIsTwoHoursFromNow = false,
    userTimeShift = 15,
    fetchUserTimeShift,
    sendOnApprovement,
    approvementModalState,
    pendingStatus,
    pendingTime
  } = props;

  const internalApprovementState = useState(false);

  const [openApprovementModal, setOpenApprovementModal] =
    approvementModalState ?? internalApprovementState;

  const [initialTime, setInitialTime] = useState<
    string | Moment | undefined | null
  >(
    combineDateAndTime(currentDate, punchTime)
      ? combineDateAndTime(currentDate, punchTime)
      : inValue
      ? null
      : (combineDateAndTime(currentDate, inValue) as Moment | undefined) ?? null
  );
  const [selectedTime, setSelectedTime] = useState<
    string | Moment | undefined | null
  >(
    combineDateAndTime(currentDate, punchTime)
      ? combineDateAndTime(currentDate, punchTime)
      : inValue
      ? null
      : (combineDateAndTime(currentDate, inValue) as Moment | undefined) ?? null
  );
  const [userTimeShiftValue, setUserTimeShiftValue] =
    useState<number>(userTimeShift);

  function _onChange(date: Moment | undefined | null) {
    const _date = date;
    console.log("_onChange", _date?.format());
    setSelectedTime(_date);
  }

  // Controlla se l'orario è "in avanti":
  // - Se esiste una timbratura originale: il nuovo orario non può essere dopo di essa (spostamento in avanti)
  // - Se non esiste timbratura (punch nuovo): il nuovo orario non può essere nel futuro rispetto all'ora attuale
  const isFutureTime = (time: Moment | null | undefined): boolean => {
    if (!time) return false;
    const selectedDateTime = moment(currentDate).set({
      hour: time.hour(),
      minute: time.minute(),
      second: time.second()
    });

    if (punchTime) {
      const originalPunchDateTime = combineDateAndTime(currentDate, punchTime);
      return (
        originalPunchDateTime != null &&
        selectedDateTime.isAfter(originalPunchDateTime)
      );
    }

    // Nessuna timbratura esistente: blocca solo orari futuri rispetto all'ora attuale
    return selectedDateTime.isAfter(moment());
  };

  const onCheckTimeValidation = (time: Moment | null | undefined) => {
    if (!time) return false;
    if (!checkIsTwoHoursFromNow) return true;
    if (!userTimeShiftValue) return false;
    if (canEdit) return true;

    // Crea il timestamp completo con data + ora per confronto accurato
    const selectedDateTime = moment(currentDate).set({
      hour: time.hour(),
      minute: time.minute(),
      second: time.second()
    });

    const now = moment();
    const timeShiftValue = moment().subtract(userTimeShiftValue, "minutes");

    // Se la data/ora selezionata è nel futuro, non è valida (gestito separatamente)
    if (selectedDateTime.isAfter(now)) {
      return false;
    }

    // Se la data/ora selezionata è oltre il limite consentito nel passato, non è valida
    if (selectedDateTime.isBefore(timeShiftValue)) {
      return false;
    }

    // Altrimenti è valida
    return true;
  };

  const _sendOnApprovement = async () => {
    if (!sendOnApprovement) return;

    const momentTime = selectedTime as Moment;

    // Usa il name corretto passato come prop (in o out)
    await sendOnApprovement(momentTime, name);

    // Resetta il valore al punchTime originale
    const originalTime = combineDateAndTime(currentDate, punchTime);
    setSelectedTime(originalTime);
    setInitialTime(originalTime);

    // Chiude la modale dopo l'invio
    setOpenApprovementModal(false);
  };

  const onAccept = (
    date: Moment | undefined | null,
    keyboardInputValue?: string | undefined
  ) => {
    console.log("onAccept", date?.format());
    if (inValue) {
      const inDate = combineDateAndTime(currentDate, inValue);
      const newValue = combineDateAndTime(
        currentDate,
        date?.format("HH:mm:ss")
      ) as Moment;
      if (newValue.isBefore(inDate)) {
        toast.warn(
          "L'ora di uscita deve essere superiore a quella di entrata.",
          {
            position: toast.POSITION.TOP_CENTER
          }
        );
        setInitialTime(
          combineDateAndTime(currentDate, punchTime)
            ? combineDateAndTime(currentDate, punchTime)
            : undefined
        );
        setSelectedTime(
          combineDateAndTime(currentDate, punchTime)
            ? combineDateAndTime(currentDate, punchTime)
            : undefined
        );
        return;
      }
    }

    if (!onCheckTimeValidation(date)) {
      // Se l'orario è nel futuro, blocca sempre (anche per le approvazioni)
      console.log("isFutureTime: >>", isFutureTime(date));
      if (checkIsTwoHoursFromNow && !canEdit && isFutureTime(date)) {
        toast.error("Non è possibile inserire una timbratura futura.", {
          position: toast.POSITION.TOP_CENTER
        });
        setSelectedTime(initialTime);
        return;
      }
      // gestire il caso in cui mandare una timbratura in approvazione
      setOpenApprovementModal(true);
      // toast.error(
      //   `Non è possibile marcare ${userTimeShiftValue} minuti prima o dopo la data corrente.`,
      //   {
      //     position: toast.POSITION.TOP_CENTER
      //   }
      // );
      // setSelectedTime(initialTime);
      return;
    }

    setInitialTime(date);
    onChange(date as Moment | null, name);
  };

  // Determina il colore del bordo e il testo della label in base allo stato
  const getBorderColor = () => {
    if (pendingStatus === "pending") return "#ed6c02"; // warning
    if (pendingStatus === "approved") return "#2e7d32"; // success
    if (pendingStatus === "rejected") return "#d32f2f"; // error
    return undefined;
  };

  const getStatusText = () => {
    const timeText = pendingTime
      ? `: ${moment(pendingTime, "HH:mm:ss.SSS").format("HH:mm")}`
      : "";
    if (pendingStatus === "pending") return `In attesa${timeText}`;
    if (pendingStatus === "approved") return `Approvata`;
    if (pendingStatus === "rejected") return `Rifiutata${timeText}`;
    return "";
  };

  const borderColor = getBorderColor();
  const statusText = getStatusText();

  return (
    <>
      <MobileTimePicker
        onChange={_onChange}
        onAccept={onAccept}
        disabled={canEdit ? false : disabled}
        label={label}
        minutesStep={minuteStep}
        value={selectedTime as any}
        slotProps={{
          textField: {
            helperText: statusText || " ",
            sx: borderColor
              ? {
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: borderColor,
                      borderWidth: 2
                    },
                    "&:hover fieldset": {
                      borderColor: borderColor
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: borderColor
                    }
                  },
                  "& .MuiInputLabel-root": {
                    color: borderColor,
                    fontWeight: 600
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: borderColor
                  },
                  "& .MuiFormHelperText-root": {
                    color: borderColor,
                    fontWeight: 600
                  }
                }
              : undefined
          }
        }}
      />
      <ApprovementModal
        setVisibility={setOpenApprovementModal}
        visible={openApprovementModal}
        onSendApprovement={_sendOnApprovement}
        onCancel={() => setSelectedTime(initialTime)}
        userTimeShift={userTimeShift}
        currentTime={punchTime || pendingTime || undefined}
        selectedTime={selectedTime}
      />
    </>
  );
};

export default TimeSelector;
